import { useState, useEffect, useRef } from 'react';
import { logger } from 'src/utils/logger';
import { invokeAgent, generateSessionId } from 'src/services/agentCoreService';
import { getCognitoAuthSession } from 'src/authentication/cognito';
import { saveSession, loadSession, clearAll } from './sessionStorageService';
import { getPhoneToolImageURL } from 'src/pages/entitySearch/entitySearchConstants';
import { generateMessageId } from './constants';
import { finalizeStreamingMessages } from './messageUtils';
import type { Message } from './useChatbotLogic';

async function getCognitoUsername(): Promise<string> {
	try {
		const session = await getCognitoAuthSession();
		const fullUsername = (session as any).accessToken?.payload?.username || 'anonymous';
		const parts = fullUsername.split('_');
		return parts.length > 1 ? parts[1] : fullUsername;
	} catch (err) {
		logger.error('Failed to get Cognito username', { error: err });
		return 'anonymous';
	}
}

export const useAgentSession = (
	setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
	setIsTyping: (typing: boolean) => void,
) => {
	const sessionIdRef = useRef<string | null>(null);
	const actorIdRef = useRef<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);
	const [username, setUsername] = useState<string | null>(null);

	useEffect(() => {
		getCognitoUsername().then((uname) => {
			if (uname && uname !== 'anonymous') {
				setUsername(uname);
				setUserPhotoUrl(getPhoneToolImageURL(uname));
			}
		});
	}, []);

	useEffect(() => {
		const restoredSession = loadSession();
		if (restoredSession) {
			sessionIdRef.current = restoredSession.sessionId;
			actorIdRef.current = restoredSession.actorId;
		}
	}, []);

	const ensureSession = async () => {
		if (sessionIdRef.current && actorIdRef.current) return;

		const uname = await getCognitoUsername();
		actorIdRef.current = uname;
		sessionIdRef.current = generateSessionId(uname);
		saveSession(sessionIdRef.current, actorIdRef.current);
		setUserPhotoUrl(getPhoneToolImageURL(uname));
		logger.info('AgentCore session initialized', {
			actorId: actorIdRef.current,
			sessionId: sessionIdRef.current,
		});
	};

	const sendToAgent = async (userInput: string) => {
		await ensureSession();

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			setMessages(finalizeStreamingMessages);
		}
		const controller = new AbortController();
		abortControllerRef.current = controller;

		const messageId = generateMessageId('bot');
		const newMessage: Message = {
			id: messageId,
			text: '',
			sender: 'bot',
			timestamp: Date.now() / 1000,
			isTyping: true,
			fullContent: '',
		};
		setMessages((prev) => [...prev, newMessage]);

		try {
			const result = await invokeAgent({
				message: userInput,
				sessionId: sessionIdRef.current!,
				actorId: actorIdRef.current!,
				abortSignal: controller.signal,
				onToken: (token: string) => {
					// Drop tokens from an aborted request to avoid a stale cursor
					if (controller.signal.aborted) return;
					setMessages((prev) =>
						prev.map((msg) =>
							msg.id === messageId
								? { ...msg, text: msg.text + token, fullContent: (msg.fullContent || '') + token }
								: msg
						)
					);
				},
			});

			if (controller.signal.aborted) return;

			if (!result.text.trim()) {
				setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
			} else {
				setMessages((prev) =>
					prev.map((msg) =>
						msg.id === messageId
							? { ...msg, text: result.text, fullContent: result.text, isTyping: false, timestamp: Date.now() / 1000 }
							: msg
					)
				);
			}
		} catch (error: any) {
			// A user-triggered abort is expected — don't surface it as an error
			if (error.name === 'AbortError' || controller.signal.aborted) {
				logger.info('Agent invocation aborted by user');
				return;
			}
			logger.error('AgentCore invocation failed', { error: error.message || error, details: error.details });
			const displayMessage = error.userMessage || 'I ran into an issue processing your request. Please try again.';
			setMessages((prev) =>
				prev.map((msg) =>
					msg.id === messageId
						? { ...msg, text: displayMessage, isTyping: false }
						: msg
				)
			);
		}
		setIsTyping(false);
	};

	const resetSession = () => {
		abortControllerRef.current?.abort();
		sessionIdRef.current = null;
		actorIdRef.current = null;
		clearAll();
	};

	const stopResponse = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		setIsTyping(false);
		setMessages(finalizeStreamingMessages);
	};

	return {
		username,
		userPhotoUrl,
		sendToAgent,
		resetSession,
		stopResponse,
	};
};
